import { useEffect, useMemo, useState } from "react";
import MetricCard from "../../components/MetricCard.jsx";
import PlusNudge from "../../components/PlusNudge.jsx";
import { useI18n } from "../../i18n.jsx";
import { passagesJsonPath } from "../../lib/legacyPaths.js";
import { compareWritingToSource, countWords, getWords } from "../../lib/textMetrics.js";

const COPYING_LIMIT = 0.1;
const COPYING_NGRAM_SIZE = 3;

function adviceBarPercent(value, maximum) {
  const max = Math.max(Number(maximum) || 0, Number(value) || 0, 1);
  return Math.max(2, Math.min(100, Math.round(((Number(value) || 0) / max) * 100)));
}

function SummaryAdviceModal({ advice, onClose }) {
  if (!advice) return null;

  return (
    <div className="writing-advice-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="writing-advice-dialog" role="dialog" aria-modal="true" aria-labelledby="summary-advice-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="writing-advice-close" type="button" onClick={onClose} aria-label="Close advice">×</button>
        <h2 id="summary-advice-title">{advice.title}</h2>
        <p className="writing-advice-copy">{advice.message}</p>

        {advice.bars?.length ? (
          <div className="writing-advice-chart" aria-label={advice.chartTitle}>
            <h3>{advice.chartTitle}</h3>
            <div className="writing-advice-legend">
              <span className="benchmark" />
              {advice.metricLabel}
            </div>
            <div className="writing-advice-bars">
              {advice.bars.map((bar) => (
                <div className="writing-advice-bar-row" key={bar.label}>
                  <span>{bar.label}</span>
                  <div className="writing-advice-bar-track">
                    <strong className={bar.kind || "current"} style={{ width: `${adviceBarPercent(bar.value, advice.maxValue)}%` }}>
                      {bar.display}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {advice.feedback ? <div className="writing-advice-feedback">{advice.feedback}</div> : null}
      </section>
    </div>
  );
}

export default function SummarizingPage({ level }) {
  const { t } = useI18n();
  const [passages, setPassages] = useState([]);
  const [passageIndex, setPassageIndex] = useState(0);
  const [text, setText] = useState("");
  const [adviceType, setAdviceType] = useState(null);

  useEffect(() => {
    fetch(passagesJsonPath(level.id))
      .then((response) => response.json())
      .then(setPassages)
      .catch(() => setPassages([]));
  }, [level.id]);

  const passage = passages[passageIndex];
  const wordCount = useMemo(() => countWords(text), [text]);
  const copying = useMemo(
    () => compareWritingToSource(COPYING_NGRAM_SIZE, text, passage?.passage || ""),
    [passage?.passage, text]
  );
  const copyingPercent = copying.copyPCT;
  const copyingOkay = copyingPercent <= COPYING_LIMIT;
  const inRange = passage ? wordCount >= passage.wordMin && wordCount <= passage.wordMax : false;
  const progress = passage ? Math.min(100, Math.round((wordCount / passage.wordMax) * 100)) : 0;
  const rangeDetail = passage ? t("summarizing.rangeWords", { min: passage.wordMin, max: passage.wordMax }) : t("summarizing.loading");
  const rangeStatus = !passage
    ? t("summarizing.loadingPassage")
    : wordCount < passage.wordMin
      ? t("summarizing.wordsToMinimum", { count: passage.wordMin - wordCount })
      : wordCount > passage.wordMax
        ? t("summarizing.wordsOverMaximum", { count: wordCount - passage.wordMax })
        : t("summarizing.withinRange");
  const metGoalCount = [inRange, copyingOkay].filter(Boolean).length;
  const advice = useMemo(() => {
    if (!passage || !adviceType) return null;

    if (adviceType === "length") {
      const bars = [
        { label: "Minimum Length", value: passage.wordMin, display: String(passage.wordMin), kind: "benchmark" },
        { label: "Your Length", value: wordCount, display: String(wordCount), kind: "current" },
        { label: "Maximum Length", value: passage.wordMax, display: String(passage.wordMax), kind: "benchmark" }
      ];
      return {
        title: "Advice on Length (number of words)",
        message: inRange
          ? `You wrote ${wordCount} words, which is between the recommended lengths of ${passage.wordMin} and ${passage.wordMax}. Good job.`
          : `You did not write enough words. You wrote: ${wordCount} words, but you need to write between ${passage.wordMin} and ${passage.wordMax} words. Try to write JUST the important details. Consider deleting unimportant adjectives and adverbs if you have too many words.`,
        chartTitle: "Length of Your Paragraph Versus Benchmarks",
        metricLabel: "Number of Words",
        bars,
        maxValue: Math.max(passage.wordMax, wordCount)
      };
    }

    if (adviceType === "copy") {
      const percent = copyingPercent * 100;
      const copiedWords = copying.copiedWords;
      const words = getWords(text);
      return {
        title: "Advice on Overcopying",
        message: copyingOkay
          ? "Your writing sample does NOT overcopy from the reading passage. Good job!"
          : `You have copied too much from the reading passage. Your text currently copied about ${percent.toFixed(1)}% of its words from the reading passage, but these should be less than 10% similarity. Check the graph and highlighted writing below. The phrases highlighted in red match exact phrases in the reading passage. Work with a partner or ask your teacher how you can reduce copying.`,
        chartTitle: "Length of Your Paragraph Versus Benchmarks",
        metricLabel: "Copying Percentages",
        bars: copyingOkay ? [] : [
          { label: "Maximum Copying Allowed", value: COPYING_LIMIT, display: "10%", kind: "benchmark" },
          { label: "Your Copying Amount", value: copyingPercent, display: `${percent.toFixed(1)}%`, kind: "current" }
        ],
        maxValue: Math.max(COPYING_LIMIT, copyingPercent),
        feedback: !copyingOkay && words.length ? (
          <p className="writing-highlighted-text">
            {words.map((word, index) => (
              <span key={`${word}-${index}`} className={copiedWords.has(index) ? "is-overused" : undefined}>
                {word}{" "}
              </span>
            ))}
          </p>
        ) : null
      };
    }

    return null;
  }, [adviceType, copying, copyingOkay, copyingPercent, inRange, passage, text, wordCount]);

  return (
    <section className="writing-view summarizing-view">
      <div className="setup-panel compact-panel summary-setup-card">
        <label className="field-label" htmlFor="passage-select">{t("summarizing.currentPassage")}</label>
        <select id="passage-select" value={passageIndex} onChange={(event) => setPassageIndex(Number(event.target.value))}>
          {passages.map((_, index) => (
            <option key={index} value={index}>{t("summarizing.passage", { number: index + 1 })}</option>
          ))}
        </select>
      </div>

      <div className="directions-card full-span summary-brief">
        <div>
          <p className="kicker">{t("summarizing.kicker", { level: t(level.nameKey, {}, level.label) })}</p>
          <h2>{t("summarizing.prompt")}</h2>
          <p>{passage ? t("summarizing.writeRange", { range: rangeDetail }) : t("summarizing.loadingPassages")}</p>
        </div>
        <div className="summary-brief-stats" aria-label={t("summarizing.targetsLabel")}>
          <span>
            <strong>{passages.length || "-"}</strong>
            {t("common.passages")}
          </span>
          <span>
            <strong>{passageIndex + 1}</strong>
            {t("common.selected")}
          </span>
          <span>
            <strong>{passage ? `${passage.wordMin}-${passage.wordMax}` : "-"}</strong>
            {t("common.wordTarget")}
          </span>
          <span>
            <strong>{passage ? `${metGoalCount}/2` : "-"}</strong>
            {t("writing.goalsMet")}
          </span>
        </div>
      </div>

      <article className="source-card summary-source-card">
        <div className="summary-card-header">
          <div>
            <p className="kicker">{t("summarizing.sourceText")}</p>
            <h2>{t("summarizing.readingPassage")}</h2>
          </div>
          <span>{t("summarizing.passage", { number: passageIndex + 1 })}</span>
        </div>
        <div className="summary-source-body">
          <p>{passage?.passage || t("summarizing.noPassage")}</p>
        </div>
      </article>

      <div className="editor-card summary-editor-card">
        <div className="summary-card-header">
          <div>
            <p className="kicker">{t("summarizing.yourSummary")}</p>
            <h2>{t("summarizing.workspace")}</h2>
          </div>
          <span>{t("summarizing.rangeWords", { min: wordCount, max: wordCount }).replace(`${wordCount}-${wordCount}`, String(wordCount))}</span>
        </div>
        <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder={t("writing.placeholder")} />
        <div className="summary-progress" aria-label={rangeStatus}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <p className={inRange ? "summary-range is-good" : "summary-range"}>{rangeStatus}</p>
      </div>

      <aside className="goals-card summary-goals-card">
        <div className="summary-card-header">
          <div>
            <p className="kicker">{t("summarizing.qualityChecks")}</p>
            <h2>{t("summarizing.goals")}</h2>
          </div>
        </div>
        <div className="writing-advice-panel">
          <p>{t("summarizing.advicePrompt")}</p>
          <button type="button" className="writing-advice-button" onClick={() => setAdviceType("length")}>
            {t("writing.length")}
          </button>
          <button type="button" className="writing-advice-button" onClick={() => setAdviceType("copy")}>
            {t("summarizing.copying")}
          </button>
        </div>
        <MetricCard label={t("writing.length")} value={wordCount} detail={rangeDetail} status={inRange ? "good" : "needs-work"} />
        <MetricCard label={t("summarizing.target")} value={inRange ? t("summarizing.onTrack") : t("summarizing.revise")} detail={rangeStatus} status={inRange ? "good" : "needs-work"} />
        <MetricCard label={t("summarizing.copying")} value={`${Math.round(copyingPercent * 100)}%`} detail={t("summarizing.copyingLimit")} status={copyingOkay ? "good" : "needs-work"} />
        <PlusNudge className="workspace-plus-nudge" source="writing-adfree-hint">
          {t("writing.adfreeHint")}
        </PlusNudge>
      </aside>
      <SummaryAdviceModal advice={advice} onClose={() => setAdviceType(null)} />
    </section>
  );
}
